import { CLIENT_EVENT, EventLogger } from "./eventLogger";

const STORAGE_KEY = "bricks:offline-period";

/** 오프라인 동안 "앱이 아직 살아 있다"고 표시하는 주기. */
const HEARTBEAT_MS = 30_000;

/**
 * 저장된 구간을 "끝난 것"으로 간주하는 기준.
 * 하트비트를 세 번 놓친 정도면 그 구간을 남긴 탭은 죽었다고 본다.
 * 다른 탭이 아직 살아 있으면 `lastSeenAt` 이 계속 갱신되므로 이 기준에 걸리지 않는다.
 */
const STALE_AFTER_MS = HEARTBEAT_MS * 3;

/**
 * 진행 중인 오프라인 구간. 오프라인 상태로 앱이 종료돼도 다음 실행에서
 * 이어서 보고할 수 있도록 localStorage 에 미러링한다.
 */
type PendingOfflinePeriod = {
  startedAt: number;
  /** 마지막으로 앱이 살아 있는 것이 확인된 시각. 종료 시각의 하한값으로 쓴다. */
  lastSeenAt: number;
  page: string;
};

let pending: PendingOfflinePeriod | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let isInitialized = false;
/** Private 모드 등으로 storage 를 못 쓰면 탭 간 조율을 포기하고 메모리 기준으로만 집계한다. */
let isStorageUsable = true;

function persist(): void {
  try {
    if (pending) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    isStorageUsable = false;
  }
}

function readPersisted(): PendingOfflinePeriod | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingOfflinePeriod>;
    if (typeof parsed.startedAt !== "number" || typeof parsed.lastSeenAt !== "number") return null;

    return { startedAt: parsed.startedAt, lastSeenAt: parsed.lastSeenAt, page: parsed.page ?? "" };
  } catch {
    return null;
  }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

function startHeartbeat(): void {
  if (heartbeatTimer !== null) return;
  heartbeatTimer = setInterval(touch, HEARTBEAT_MS);
}

function stopHeartbeat(): void {
  if (heartbeatTimer === null) return;
  clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function begin(): void {
  if (pending) return;

  const now = Date.now();
  // 다른 탭이 이미 같은 단절을 기록 중이면 그 구간을 이어받는다.
  // 새로 만들면 단절 1회가 탭 수만큼의 이벤트로 불어난다.
  const existing = readPersisted();
  const isExistingAlive = existing !== null && now - existing.lastSeenAt < STALE_AFTER_MS;

  pending = isExistingAlive
    ? { ...existing, lastSeenAt: now }
    : { startedAt: now, lastSeenAt: now, page: location.pathname };

  persist();
  startHeartbeat();
}

function touch(): void {
  if (!pending) return;
  pending.lastSeenAt = Date.now();
  persist();
}

function end(): void {
  if (!pending) return;

  const { startedAt, page } = pending;
  pending = null;
  stopHeartbeat();

  // 저장된 레코드를 "가져가는" 쪽만 보고한다.
  // 여러 탭이 같은 단절을 공유했다면 먼저 도착한 탭 하나만 이벤트를 남긴다.
  // storage 를 못 쓰는 환경에서는 애초에 공유가 없으므로 이 조율을 건너뛴다.
  const claimed = readPersisted();
  clearPersisted();
  if (isStorageUsable && !claimed) return;

  EventLogger.track(CLIENT_EVENT.OFFLINE_PERIOD, {
    duration_ms: Math.max(0, Date.now() - startedAt),
    page,
    ended_by: "reconnect",
    /** 온라인 복귀를 직접 관측했으므로 duration 은 정확하다. */
    is_duration_exact: true,
  });
}

/**
 * 이전 세션이 오프라인 상태로 끝난 경우, 지금(=전송 가능한 시점) 뒤늦게 보고한다.
 *
 * 앱이 언제 닫혔는지는 알 수 없으므로 마지막 하트비트까지만 계산한다.
 * 즉 이 이벤트의 duration 은 실제 오프라인 시간의 **하한값**이며,
 * 오차는 하트비트 주기(30초) 이내다.
 *
 * `lastSeenAt` 이 아직 신선하면 다른 탭이 같은 단절을 진행 중이라는 뜻이므로 건드리지 않는다.
 */
function flushInterrupted(): void {
  const previous = readPersisted();
  if (!previous) return;

  if (Date.now() - previous.lastSeenAt < STALE_AFTER_MS) return;

  clearPersisted();

  EventLogger.track(CLIENT_EVENT.OFFLINE_PERIOD, {
    duration_ms: Math.max(0, previous.lastSeenAt - previous.startedAt),
    page: previous.page,
    ended_by: "session_end",
    is_duration_exact: false,
  });
}

/**
 * 오프라인 구간(빈도 · 길이)을 수집한다.
 *
 * Sentry 는 오프라인에서 발생한 이벤트를 `beforeSend` 에서 버리고, 화면 로그도
 * 남지 않기 때문에 지금까지 오프라인 구간은 데이터에 전혀 나타나지 않았다.
 * 여기서는 "온라인으로 돌아온 시점"에 한 건으로 요약해 보내므로 전송이 보장된다.
 *
 * 앱 부팅 시 한 번만 호출한다. 중복 호출은 무시된다.
 *
 * 한계: 단절 판정은 `navigator.onLine` 기준이라, 와이파이는 붙었는데 인터넷이
 * 없는 경우(captive portal 등)를 놓친다. 화면 배너가 쓰는 `useConnectivity` 의
 * HTTP probe 와는 기준이 다르다는 점을 해석 시 감안해야 한다.
 */
export function initOfflineTracker(): void {
  if (isInitialized) return;
  isInitialized = true;

  flushInterrupted();

  if (!navigator.onLine) begin();

  window.addEventListener("offline", begin);
  window.addEventListener("online", end);
  window.addEventListener("pagehide", touch);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") touch();
  });
}
